'use client';

/**
 * StandardModeContent
 *
 * Renders the purchase flow in "modal" or "inline" display mode.
 * Contains the screen router (renderScreen switch) and wraps it
 * with FlowHeader + optional Popover for modal mode.
 */

import React from 'react';
import { Popover } from '@barges/react';
import type { FlowMode, DisplayMode } from '../types';
import type { PurchaseMetadata } from '../types/processing';
import type { MobileAppProps } from '../types/purchase';
import type { UsePurchaseFlowCoreReturn } from '../hooks/usePurchaseFlowCore';
import PurchaseDetailsScreen from '../screens/PurchaseDetailsScreen';
import PaymentMethodsListScreen from '../screens/PaymentMethodsListScreen';
import AddPaymentMethodScreen from '../screens/AddPaymentMethodScreen';
import PaymentProcessingScreen from '../screens/PaymentProcessingScreen';
import AffiliatePromoSuccessScreen from '../screens/AffiliatePromoSuccessScreen';
import { FlowHeader } from './FlowHeader';
import { SCREENS } from '../utils/constants';
import classes from '../screens/classes.module.scss';

// ============================================================================
// Types
// ============================================================================

interface StandardModeContentProps {
  core: UsePurchaseFlowCoreReturn;
  displayMode: Exclude<DisplayMode, 'button'>;
  isPayg: boolean;
  mode: FlowMode;
  bannerSlot?: React.ReactNode;
  promoFooterSlot?: React.ReactNode;
  successButtonTextKey?: string;
  mobileAppProps?: MobileAppProps;
  metadata?: PurchaseMetadata;
  processorElement: React.ReactNode;
  validationErrorPopup: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

const StandardModeContent: React.FC<StandardModeContentProps> = ({
  core,
  displayMode,
  isPayg,
  mode,
  bannerSlot,
  promoFooterSlot,
  successButtonTextKey,
  mobileAppProps,
  processorElement,
  validationErrorPopup
}) => {
  const {
    headerConfig,
    handleBackClick,
    handleCloseClick,
    renderTopOverlay,
    currentScreen,
    selectedPaymentMethod,
    paymentOptions,
    detailsSlot,
    isMinPurchase,
    loading,
    error,
    actionInProgress,
    validating,
    validationError,
    dismissValidationError,
    handlePayClick,
    onPerformSetup,
    paymentMethods,
    handleEditPaymentMethod,
    handleLoadMethods,
    addingPaymentMethodKind,
    editingPaymentSourceId,
    handlePaymentMethodAdded,
    chargeProcessing,
    statementNote,
    focusProcessingWindow,
    handleRetryClick,
    screenCallbacks,
    handlers,
    handleAddPaymentMethodClick,
    currency,
    dataLoaded,
    feeAdjustedAmount,
    bonus,
    fxRate,
    purchaseFeesText,
    purchaseDetails,
    nativeButtonReady
  } = core;

  // ==========================================================================
  // Screen Router
  // ==========================================================================

  const renderScreen = () => {
    switch (currentScreen) {
      case SCREENS.PURCHASE_DETAILS:
        return (
          <PurchaseDetailsScreen
            selectedPaymentMethod={ selectedPaymentMethod }
            paymentOptions={ paymentOptions }
            currency={ currency }
            detailsSlot={ detailsSlot }
            bannerSlot={ bannerSlot }
            promoFooterSlot={ promoFooterSlot }
            isPayg={ isPayg }
            isMinPurchase={ isMinPurchase }
            loading={ loading || !dataLoaded }
            error={ error }
            mode={ mode }
            price={ feeAdjustedAmount }
            bonus={ bonus * fxRate }
            purchaseFeesText={ purchaseFeesText }
            creditsRequested={ purchaseDetails?.fees.creditsRequested }
            actionInProgress={ actionInProgress || validating }
            validating={ validating }
            validationError={ mobileAppProps ? validationError : null }
            onDismissValidationError={ mobileAppProps ? dismissValidationError : undefined }
            onPaymentMethodClick={ handlers.handlePaymentMethodClick }
            onAddPaymentMethodClick={ handleAddPaymentMethodClick }
            onPayClick={ handlePayClick }
            onPerformSetup={ onPerformSetup }
            nativeButtonReady={ nativeButtonReady }
          />
        );

      case SCREENS.PAYMENT_METHODS_LIST:
        return (
          <PaymentMethodsListScreen
            paymentMethods={ paymentMethods }
            selectedMethodId={ selectedPaymentMethod?.id }
            onSelect={ handlers.handlePaymentMethodSelect }
            onAddClick={ handleAddPaymentMethodClick }
            availableOptions={ paymentOptions }
            isPayg={ isPayg }
            actionInProgress={ actionInProgress }
            onPerformSetup={ onPerformSetup }
            onEditPaymentMethod={ handleEditPaymentMethod }
            onLoadMethods={ handleLoadMethods }
          />
        );

      case SCREENS.ADD_CREDIT_CARD:
        if (!addingPaymentMethodKind) {
          return null;
        }
        return (
          <AddPaymentMethodScreen
            onComplete={ handlePaymentMethodAdded }
            onError={ handlers.handleError }
            editPaymentSourceId={ editingPaymentSourceId }
            isPayg={ isPayg }
          />
        );

      case SCREENS.PAYMENT_PROCESSING:
        if (mode === 'affiliatePromo' && chargeProcessing.state.status === 'success') {
          return (
            <AffiliatePromoSuccessScreen
              currency={ currency }
              totalBalance={  feeAdjustedAmount + (bonus * fxRate) }
              bonus={ bonus * fxRate }
              successButtonTextKey={ successButtonTextKey }
              onOkClick={ screenCallbacks.handleOkClick }
            />
          );
        }
        return (
          <PaymentProcessingScreen
            chargeState={ chargeProcessing.state }
            onFocusProcessingWindow={ selectedPaymentMethod?.kind === 'pp' ? focusProcessingWindow : undefined }
            statementNote={ statementNote }
            successButtonTextKey={ successButtonTextKey }
            showInlineErrorIcon={ displayMode !== 'modal' }
            onOkClick={ screenCallbacks.handleOkClick }
            onRetryClick={ handleRetryClick }
            onCloseClick={ screenCallbacks.handleCloseClick }
            onSpendingLimitsClick={ screenCallbacks.handleSpendingLimitsClick }
          />
        );

      default:
        return null;
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================

  const uiContent = (
    <div className={ classes.flowContent } data-testid="purchase-flow-content">
      <FlowHeader
        titleKey={ headerConfig.titleKey }
        showBackButton={ headerConfig.showBackButton }
        showCloseButton={ headerConfig.showCloseButton }
        onBack={ handleBackClick }
        onClose={ handleCloseClick }
      />
      {renderScreen()}
    </div>
  );

  if (displayMode === 'modal') {
    return (
      <>
        {processorElement}
        <Popover
          isOpen
          className={ classes.popover }
          centered={ currentScreen === SCREENS.PAYMENT_PROCESSING }
          contentClassName={ classes.popoverContent }
          overlayClassName={ classes.errorOverlayContainer }
          renderTopOverlay={ renderTopOverlay }
        >
          {uiContent}
        </Popover>
        {validationErrorPopup}
      </>
    );
  }

  return (
    <>
      {processorElement}
      {uiContent}
      {validationErrorPopup}
    </>
  );
};

export default StandardModeContent;
